/* eslint-disable no-param-reassign */
import { INJECT_UICORE_FORM, INJECT_UICORE_FORM_GET_OVERRIDE_PROPS } from "../../../../lib/utils/consts";
import { getFitDataLayout, getCompPathParentInfo, vueDeepAssign } from "../../../../lib/utils";
import { UICorePlugin } from "../../../../lib/Plugin";
import WForm from "../../../../lib/builtin/components/Form";
import FormEditorBase from './FormEditorBase';
import getFormItemLayout from './getFormItemLayout';
import { vueMakeExtendFactory } from "../../../../lib/utils/vue";
import { arrayify } from "../../../../lib/utils/array";
import { formOverridePropIds } from './formOverrideProps';
import FormContainerBase from "../../../../lib/builtin/plugins/form/FormContainerBase";
/** 修改propsSchema中的某些field 并进行必要的浅拷贝保证不修改原始meta */
const changeMetaPropSchemaFields = (meta, fieldId, fieldObject) => {
  if (!meta || !meta.propsSchema || !Array.isArray(meta.propsSchema.fields)) return meta;
  const fieldIdArray = arrayify(fieldId);
  if (fieldIdArray.length === 0) return meta;
  // 将修改过的field 按照原来的顺序patch进fields里 并浅拷贝
  const shallowFields = meta.propsSchema.fields.map(f => {
    const needMerge = fieldIdArray.includes(`${f.id}`);
    if (needMerge) return {
      ...f,
      ...fieldObject
    };
    return f;
  });
  // 合并 同时浅拷贝propSchema 以及整个meta
  const shallowMeta = {
    ...meta,
    propsSchema: {
      ...meta.propsSchema,
      fields: shallowFields
    }
  };
  return shallowMeta;
};
export class FormPlugin extends UICorePlugin {
  install(uc) {
    const extendFormEditor = vueMakeExtendFactory(FormEditorBase);
    const extendFormContainer = vueMakeExtendFactory(FormContainerBase);
    const hookAsForm = (meta, extendComp) => {
      // 更改'required', 'readonly', 'hasSuccessFeedback' 的输入为三态选择框
      const newMeta = changeMetaPropSchemaFields(meta, formOverridePropIds, {
        ui: {
          type: 'multiRatioInput'
        }
      });
      return {
        ...newMeta,
        component: extendComp(newMeta.component)
      };
    };
    // 在载入 isFormEditor 的组件时，添加额外的生命周期
    uc.hooks.componentMetaListAfterLoad.tap('FormPlugin', list => list.map(meta => {
      if (meta.isFormEditor) return hookAsForm(meta, extendFormEditor);
      if (meta.isFormContainer) return hookAsForm(meta, extendFormContainer);
      return meta;
    }));
    // 对每一个 CRenderer，注入 ucRootForm
    uc.hooks.pageletRendererCreated.tap('FormPlugin', renderer => {
      if (renderer.ucIsRoot) {
        // 这个是根页面片，需要创建一个 ucRootForm
        const ucRootForm = new WForm({
          parent: renderer,
          propsData: {
            ...uc.config.rootFormProps,
            isRoot: true
          }
        });
        renderer.ucRootForm = ucRootForm;
        renderer.$once('hook:beforeDestroy', () => {
          ucRootForm.$destroy();
        });
        // 使用 Vue2 的注入机制，提供到子元素
        renderer._provided = {
          ...renderer._provided,
          [INJECT_UICORE_FORM]: ucRootForm,
          [INJECT_UICORE_FORM_GET_OVERRIDE_PROPS]: null
        };
      } else {
        // 继承的方式
        renderer.ucRootForm = renderer.ucRootRenderer.ucRootForm;
      }
    });
    // 提供“表单项”的东西
    uc.hooks.fitDataAfterAll.tap('FormPlugin', (result, req) => {
      if (req.options.excludeFormItem) return result;
      // 首先，找到最优的 editor
      let editor;
      let editorIdx = -1;
      result.forEach((it, idx) => {
        if (it.type !== 'editor') return;
        if (!editor || editor.order > it.order) {
          editor = it;
          editorIdx = idx;
        }
      });
      if (!editor) return result;
      const {
        layout
      } = getFitDataLayout(editor);
      // 然后为他包一个壳子
      const newSuggestion = {
        name: `带标签的${editor.name}`,
        desc: editor.desc,
        icon: editor.icon,
        order: editor.order,
        layout: getFormItemLayout(req.dataStub, [layout], editor),
        type: 'editor',
        subTitle: '',
        onApply: () => void 0,
        inheritAttrs: true
      };
      // 如果当前数据是 object 或者 array 的话，“带标签的XXX”要放在原本的编辑器组件之后
      const insertIdxOffset = ['any', 'object', 'array'].includes(req.dataSchema.type) ? 1 : 0;
      result.splice(editorIdx + insertIdxOffset, 0, newSuggestion);
      return result;
    });
    // 设置绑定的时候，如果组件是 w-form-item 里面的，则顺便修改 w-form-item 的标签
    uc.hooks.setBinding.tap('FormPlugin', async req => {
      var _getCompPathParentInf, _designer$getLayoutAt;
      const {
        dataStub,
        designer
      } = req;
      // 总开关
      if (!designer.designerConfig.formItemAutoSyncTitle) return;
      const parentPath = dataStub && req.isMainBinding && ((_getCompPathParentInf = getCompPathParentInfo(req.compPath)) === null || _getCompPathParentInf === void 0 ? void 0 : _getCompPathParentInf.parentCompPath);
      const parentType = parentPath && ((_designer$getLayoutAt = designer.getLayoutAtPath(parentPath)) === null || _designer$getLayoutAt === void 0 ? void 0 : _designer$getLayoutAt.type);
      if (!dataStub || !parentPath || !parentType) return;
      // 设置绑定的时候，如果组件是 w-form-item 里面的，则顺便修改 w-form-item 的标签
      if (parentType === 'w-form-item') {
        await designer.modifyComp(parentPath, parent => {
          vueDeepAssign(parent, {
            props: {
              label: dataStub.getTitle(),
              description: dataStub.schema.description || ''
            }
          });
        });
      }
    });
  }
}